<template>
	<div id="volatility-return">
		<h3 class="module-title">
			Volatility & Return
			<slot name="superscript1" />
		</h3>
		<a v-if="isOlderThan10Years" class="modal-link above-chart" @click="emitOpenModal">
			<i class="icon icon-zoom-in" />
			VIEW LARGER CHART
		</a>
		<ReadMoreLink />
		<div v-if="isOlderThan10Years">
			<div v-if="hasDualCharts">
				<a class="sub-title" :class="{active: isEquity}" @click="setAssetClass('equity')">Equities</a>
				<a
					class="sub-title divider-line"
					:class="{active: !isEquity}"
					@click="setAssetClass('fixedIncome')"
				>Fixed Income</a>
			</div>
			<div v-else>
				<div v-if="isEquity" class="sub-title">Equities</div>
				<div v-else class="sub-title">Fixed Income</div>
			</div>
			<div class="legend-text">
				<span class="emphasized">{{ chartLegendText[0] }}</span>
				<span>{{ chartLegendText[1] }}</span>
				<slot name="superscript2" />
				<slot name="standardDeviation" />
				<span>{{ chartLegendText[2] }}</span>
			</div>
			<ul>
				<li v-for="(item, index) in chartLegendItems" :key="index">
					<!-- wrap the first word with the colored shape so they wrap together -->
					<span class="nowrap">
						<div :class="`colored-${item.shape}-${item.color}`" />
						{{ item.label.split(' ')[0] }}
					</span>
					<!-- render the text (while dropping the first and last word) -->
					<span> {{ item.label.split(' ').slice(1, item.label.split(' ').length - 1).join(' ') }}</span>
					<!-- render the final word (if text length has more than one word) and wrap with the superscript and iball if they exist -->
					<span class="nowrap">
						{{ item.label.split(' ').length > 1 ? item.label.split(' ')[item.label.split(' ').length - 1] : '' }}
						<slot v-if="index== 1" name="superscript3" />
						<slot v-if="index== 1" name="volatilityIndex" />
					</span>
					<!-- if there is no empty element (without the no wrap property) then the dot and first word would wrap with the last word + superscript of the previous list item -->
					<span />
				</li>
			</ul>
			<div id="volatility-chart-container" v-resize="handleChartLoad">
				<img ref="mainChart" :src="activeChart" >
			</div>
			<div
				class="volatility-as-of-dates"
			>Standard Deviation as of {{ deviationAsOfDate }} (updated monthly). Annualized return as of {{ returnAsOfDate }} (updated monthly).</div>
			<a href="#" class="modal-link below-chart" @click="emitOpenModal">
				<i class="icon icon-zoom-in" />
				VIEW LARGER CHART
			</a>
		</div>
		<div
			v-else
			class="error-message"
		>Volatility &#38; Return chart is not available for {{ fundLabel }} less than 10 years old.</div>
	</div>
</template>

<script>
import ReadMoreLink from '_components_/Shared/ReadMoreLink';
import * as tooltipsjs from '_assets_/js/tooltips.js';
import resize from 'vue-resize-directive';
import * as utils from '../../../shared/utils/utils';
export default {
	name: 'VolatilityReturn',
	directives: {
		resize
	},
	components: {
		ReadMoreLink
	},
	props: {
		widgetData: Object,
		isCIT: Boolean
	},
	data() {
		return {
			scale: 1,
			chartEventsAttached: false,
			assetClass: 'equity'
		};
	},
	computed: {
		//This is a rather interesting method of finding the age of something from an inception date
		//See the second answer here https://stackoverflow.com/questions/9/how-do-i-calculate-someones-age-in-c
		isOlderThan10Years() {
			return utils.olderThan10Years(this.inceptionDate);
		},
		deviationAsOfDate() {
			return (
				(this.widgetData && this.widgetData.deviationAsOfDate) || '--'
			);
		},
		returnAsOfDate() {
			return (this.widgetData && this.widgetData.returnAsOfDate) || '--';
		},
		//The label for the light gray 'other' legend item
		//Defaults to 'Other AF funds' but you can pass in custom text
		otherLabel() {
			return (
				(this.widgetData && this.widgetData.otherLabel) ||
				'Other AF funds'
			);
		},
		//The label that appears in the 'less than 10' text
		//Default to funds but you can pass in custom text like strategies
		fundLabel() {
			return (this.widgetData && this.widgetData.fundLabel) || 'funds';
		},
		isEquity() {
			let isEquity = true;
			const assetClass =
				(this.widgetData && this.widgetData.assetClass) || 'Equity';
			if (assetClass === 'Hybrid') {
				isEquity = this.assetClass === 'equity';
			} else {
				isEquity = assetClass === 'Equity';
			}
			return isEquity;
		},
		chartLegendText() {
			const equityText =
				'and 10-year annualized return of the equity funds.';
			const fixedIncomeText =
				'and 10-year annualized return of the fixed income funds.';
			const shareClass =
				(this.widgetData && this.widgetData.shareClass) || '';
			const result = [
				`For Class ${shareClass} Shares,`,
				'this chart tracks the 10-year standard deviation',
				''
			];
			if (this.isEquity) {
				result[2] = equityText;
			} else {
				result[2] = fixedIncomeText;
			}
			if (this.isCIT) {
				result[0] = '';
				result[1] = 'This chart tracks the 10-year standard deviation';
				result[2] =
					'and 10-year annualized net return of equity strategies.';
			}
			return result;
		},
		chartLegendItems() {
			const fund = {
				label: (this.widgetData && this.widgetData.classSymbol) || '',
				shape: 'circle',
				color: 'blue'
			};
			const index = {
				label: (this.indexData && this.indexData.FundName) || '',
				shape: 'square',
				color: 'gray'
			};
			const otherFunds = {
				label: this.otherLabel,
				shape: 'circle',
				color: 'light-gray'
			};
			return [fund, index, otherFunds];
		},
		inceptionDate() {
			return this.widgetData && this.widgetData.inceptionDate;
		},
		hasDualCharts() {
			return (!!this.widgetData && this.widgetData.equityChart) || false;
		},
		activeChart() {
			let chart = '';
			if (this.widgetData) {
				if (this.widgetData.chart) {
					chart = this.widgetData.chart;
				} else if (this.isEquity) {
					chart = this.widgetData.equityChart;
				} else {
					chart = this.widgetData.fixedIncomeChart;
				}
			}
			return chart;
		},
		strategyData() {
			let strategyData = {};
			if (this.widgetData) {
				if (this.widgetData.strategyData) {
					strategyData = this.widgetData.strategyData;
				} else if (this.isEquity) {
					strategyData = this.widgetData.equityStrategyData;
				} else {
					strategyData = this.widgetData.fixedIncomeStrategyData;
				}
			}
			return strategyData;
		},
		indexData() {
			let indexData = {};
			if (this.widgetData) {
				if (this.widgetData.indexData) {
					indexData = this.widgetData.indexData;
				} else if (this.isEquity) {
					indexData = this.widgetData.equityIndexData;
				} else {
					indexData = this.widgetData.fixedIncomeIndexData;
				}
			}
			return indexData;
		}
	},
	mounted() {
		this.setResize();
	},
	watch: {
		activeChart: {
			handler() {
				this.$nextTick(() => {
					this.handleChartLoad();
					this.setResize();
				});
			}
		}
	},
	methods: {
		setResize() {
			const chart = document.getElementById('volatility-chart-container');
			if (chart) {
				chart.childNodes[0].addEventListener(
					'resize',
					this.handleChartLoad
				);
			}
		},
		handleChartLoad() {
			this.setChartScale();
			this.attachChartEvents();
		},
		emitOpenModal() {
			const data = { modal: 'charts modal', activeTab: 'volatility' };
			this.$emit('openModal', data);
		},
		setAssetClass(assetClass) {
			this.assetClass = assetClass;
			this.attachChartEvents();
		},
		async removeChartAnchors(parent) {
			const chartAnchors = [
				...parent.getElementsByClassName('chart-point'),
				...parent.getElementsByClassName('toolTip')
			];
			for (const item of chartAnchors) {
				await item.parentNode.removeChild(item);
			}
		},
		async attachChartEvents() {
			const strategyData = this.strategyData || {};
			const indexData = this.indexData || {};
			const chartContainer = document.getElementById(
				'volatility-chart-container'
			);
			await this.removeChartAnchors(chartContainer);
			for (let i = 0; i < strategyData.length; i++) {
				const dataPoint = strategyData[i];
				const chartAnchor = this.createChartAnchor(
					dataPoint,
					chartContainer
				);
				if (chartAnchor) {
					chartContainer.appendChild(chartAnchor);

					const xScale = dataPoint.X * this.scale;
					const yScale = dataPoint.Y * this.scale;
					const toolTip = tooltipsjs.createTooltip(
						xScale,
						yScale,
						this.createToolTipBody(dataPoint)
					);

					toolTip.classList.add('toolTip');
					chartContainer.appendChild(toolTip);
					chartAnchor.addEventListener(
						'mouseenter',
						this.showToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'mouseleave',
						this.hideToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'touchstart',
						this.toggleToolTip.bind(null, toolTip),
						false
					);
				}
			}
			if (indexData.X && indexData.Y) {
				const chartAnchor = this.createChartAnchor(
					indexData,
					chartContainer
				);
				if (chartAnchor) {
					chartContainer.appendChild(chartAnchor);
					const indexDataX = indexData.X * this.scale;
					const indexDataY = indexData.Y * this.scale;
					const toolTip = tooltipsjs.createTooltip(
						indexDataX,
						indexDataY,
						this.createToolTipBody(indexData)
					);

					toolTip.classList.add('toolTip');
					chartContainer.appendChild(toolTip);
					chartAnchor.addEventListener(
						'mouseenter',
						this.showToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'mouseleave',
						this.hideToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'touchstart',
						this.toggleToolTip.bind(null, toolTip),
						false
					);
				}
			}
			this.chartEventsAttached = true;
		},
		createChartAnchor(dataPoint, chartContainer) {
			/*eslint-disable*/
			const chartContainerDimenions = chartContainer.getBoundingClientRect();
			const chartMaxHeight = chartContainerDimenions.height;
			const chartMaxWidth = chartContainerDimenions.width;
			const chartAnchor = document.createElement('a');
			const xScale = (dataPoint.X - 5) * this.scale;
			const yScale = dataPoint.Y * this.scale - 4;
			const top = `top:${yScale}px`;
			const left = `left:${xScale}px`;
			const widthAndHeight = `height: ${10 * this.scale}px; width: ${10 *
				this.scale}px;`;
			chartAnchor.classList.add('chart-point');
			chartAnchor.setAttribute(
				'style',
				`${top}; ${left}; position: absolute; ${widthAndHeight}`
			);

			return yScale > chartMaxHeight || xScale > chartMaxWidth
				? null
				: chartAnchor;
		},
		createToolTipBody(dataPoint) {
			const body = document.createElement('div');
			const innerContainer = document.createElement('div');
			innerContainer.style.marginTop = '3px';
			innerContainer.style.borderTop = `3px solid ${this.getColor(
				dataPoint.Objective
			)}`;
			innerContainer.style.padding = '5px';

			const title = document.createElement('div');
			const titleText = document.createTextNode(
				dataPoint.Name || dataPoint.FundName || 'Undefined Name'
			);
			title.appendChild(titleText);
			title.style.fontFamily = 'Avenir Next LT W01 Demi';
			title.style.whiteSpace = 'nowrap';

			innerContainer.appendChild(title);
			innerContainer.appendChild(this.getAnnualReturn(dataPoint));
			innerContainer.appendChild(this.getStdDeviation(dataPoint));
			body.appendChild(innerContainer);

			return body;
		},
		// TODO: merge getAnnualReturn and getStdDeviation into one function
		getAnnualReturn(dataPoint) {
			const annualReturn =
				dataPoint.AnnualReturn ||
				dataPoint.AnnualizedReturn10Yr ||
				'--';
			const annualData = document.createElement('span');
			const annualDataText = document.createTextNode(annualReturn);
			annualData.appendChild(annualDataText);
			annualData.style.fontFamily = 'Avenir Next LT W01 Demi';
			annualData.style.paddingRight = '3px';

			const annualLabel = document.createElement('span');
			const annualText = document.createTextNode(
				'10-Yr Annualized Return'
			);
			annualLabel.appendChild(annualText);
			annualLabel.style.color = '#666';
			annualLabel.style.fontSize = '12px';

			const annual = document.createElement('div');
			annual.appendChild(annualData);
			annual.appendChild(annualLabel);
			annual.style.borderBottom = '1px solid #ccc';
			annual.style.marginBottom = '5px';
			annual.style.paddingBottom = '5px';

			return annual;
		},
		getStdDeviation(dataPoint) {
			const standardDeviation =
				dataPoint.StD || dataPoint.StandardDeviation10Yr || '--';
			const stdData = document.createElement('span');
			const stdDataText = document.createTextNode(standardDeviation);
			stdData.appendChild(stdDataText);
			stdData.style.fontFamily = 'Avenir Next LT W01 Demi';
			stdData.style.paddingRight = '3px';

			const stdLabel = document.createElement('span');
			const stdText = document.createTextNode('10-Yr Standard Deviation');
			stdLabel.appendChild(stdText);
			stdLabel.style.color = '#666';
			stdLabel.style.fontSize = '12px';

			const std = document.createElement('div');
			std.appendChild(stdData);
			std.appendChild(stdLabel);

			return std;
		},
		showToolTip(toolTip) {
			this.hideAllToolTips();
			toolTip.style.display = 'block';
			const leftModify = toolTip.offsetWidth / 2;
			const arrowModify = leftModify - 20;
			const topModify = toolTip.offsetHeight + 30;
			const leftValue = toolTip.dataset.left - leftModify;

			const children = toolTip.childNodes;
			children[1].style.left = `${arrowModify}px`;
			children[2].style.left = `${arrowModify + 1}px`;

			toolTip.style.top = `${toolTip.dataset.top - topModify}px`;
			toolTip.style.left = `${leftValue}px`;
			toolTip.style.whiteSpace = 'nowrap';

			const chartContainer = document.getElementById(
				'volatility-chart-container'
			);
			const containerWidth = chartContainer.offsetWidth;
			const rightFlush = containerWidth - toolTip.offsetWidth;

			if (leftValue < -13) {
				const adjustArrow = -13 - leftValue;
				const arrowLeft = parseInt(children[1].style.left, 10);
				toolTip.style.left = '-13px';
				children[1].style.left = `${arrowLeft - adjustArrow}px`;
				children[2].style.left = `${arrowLeft - adjustArrow + 1}px`;
			}
			if (leftValue > rightFlush) {
				const adjustArrow = rightFlush - leftValue;
				const arrowLeft = parseInt(children[1].style.left, 10);
				toolTip.style.left = `${rightFlush}px`;
				children[1].style.left = `${arrowLeft - adjustArrow}px`;
				children[2].style.left = `${arrowLeft - adjustArrow + 1}px`;
			}
		},
		hideToolTip(toolTip) {
			toolTip.style.display = 'none';
		},
		toggleToolTip(toolTip) {
			if (toolTip.style.display === 'none') {
				this.showToolTip(toolTip);
			} else {
				this.hideToolTip(toolTip);
			}
		},
		hideAllToolTips() {
			const tips = document.getElementsByClassName('toolTip');
			for (let i = 0; i < tips.length; i++) {
				tips[i].style.display = 'none';
			}
		},
		setChartScale() {
			//From the chart inputs in the Node API
			const originalWidth = 485;
			const chartContainer = document.getElementById(
				'volatility-chart-container'
			);
			const containerWidth = chartContainer.offsetWidth;
			const scale = containerWidth / originalWidth;
			if (scale <= 0) {
				this.scale = 1;
			} else {
				this.scale = scale;
			}
		},
		getColor(objective) {
			const colors = {
				Growth: '#1E5AA0',
				'Growth &amp Income': '#009ADF',
				'Equity-Income': '#37B8A9',
				'Money Market Funds': '#7C8A8D',
				Bond: '#773C7F',
				Balanced: '#37B8A9',
				'Tax-Exempt Bond': '#773C7F',
				'Portfolio Series': '#00736C',
				'Retirement Target Date': '#00736C',
				'College Target Date': '#00736C',
				index: '#333',
				default: '#009adf'
			};

			if (objective && Object.prototype.hasOwnProperty.call(colors, objective)) {
				return colors[objective];
			}
			return colors['default'];
		}
	}
};
</script>

<style lang='less' scoped>
@import '~_css_/common.less';
@import '~_css_/variables.less';
#volatility-return {
	a {
		color: #1d5799;
	}
	//this orverrides CG's style on a plain ul element
	ul {
		margin: 0;
		li span::after {
			content: ' ';
		}
	}
	.disclosure-link {
		clear: both;
		color: #1d5799;
		display: block;
		font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
		font-size: 14px;
		font-weight: 700;
		margin-bottom: 13px;
	}
	.sub-title {
		display: inline-block;
		font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
		font-size: 14px;
		margin-bottom: 10px;
		font-weight: 500;
		&.active {
			color: @dark-gray;
		}
	}
	.legend-text {
		color: @dark-gray;
		font-family: AvenirNextLTW01-Regular, Arial;
		margin-bottom: 20px;
		span.emphasized {
			font-weight: 700;
		}
		span::after {
			content: ' ';
		}
	}
	ul {
		list-style: none;
		padding: 0;
		li {
			padding: 0 5px;
			color: @gray;
			font-family: AvenirNextLTW01-Regular, Arial;
			font-size: 12px;
			line-height: 20px;
			display: inline;
			div {
				width: 10px;
				height: 10px;
				margin-right: 5px;
				display: inline-block;
				&.colored-circle-light-gray {
					border-radius: 5px;
					background-color: #ccc;
				}
				&.colored-square-gray {
					background-color: @gray;
				}
				&.colored-circle-blue {
					border-radius: 5px;
					background-color: #009adf;
				}
			}
		}
	}
	#volatility-chart-container {
		position: relative;
		width: 100%;
		img {
			width: 100%;
		}
	}
	.error-message {
		font-family: @avenir-regular;
		margin-top: 35px;
	}
	.volatility-as-of-dates {
		margin-top: 15px;
		font-size: 12px;
		font-family: 'AvenirNextLTW01-Condens 721299', Arial;
		color: @gray;
	}
	.below-chart {
		font-family: @avenir-demi-sans;
		display: none;
		margin-top: 15px;
	}
	.above-chart {
		font-family: @avenir-demi-sans;
		float: right;
		margin: 13px 0px 20px 0px;
	}
}
@media (max-width: 768px) {
	#volatility-return {
		.below-chart {
			display: inline-block;
		}
		.above-chart {
			display: none;
		}
	}
}
</style>
